import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatDateTime } from "../app-bindings/attachment-state-ui";
import {
  useAsynchronousExecutions,
  type AsynchronousExecutionKind,
  type AsynchronousExecutionState,
} from "../app-bindings/hooks/useAppBindings";
import {
  AsynchronousExecutionStatePill,
  asynchronousExecutionKindLabel,
  asynchronousExecutionReasonLabel,
  attachmentTransferProgressText,
} from "./asynchronous-execution-ui";

const KINDS: AsynchronousExecutionKind[] = [
  "attachment_storage_alignment",
  "attachment_recovery_export",
  "attachment_environment_restore",
  "attachment_environment_restore_preview",
  "orphan_record_folder_cleanup",
];
const STATES: AsynchronousExecutionState[] = [
  "queued", "running", "blocked", "cancel_requested", "completed", "failed", "canceled",
];

export function AsynchronousExecutionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const kind = asKind(searchParams.get("executionKind"));
  const state = asState(searchParams.get("executionState"));
  const kintoneAppId = searchParams.get("kintoneAppId") ?? undefined;
  const filters = useMemo(() => ({
    kintoneAppId,
    executionKind: kind,
    executionState: state,
    limit: "50",
  }), [kintoneAppId, kind, state]);
  const executions = useAsynchronousExecutions(filters);

  function setFilter(name: "executionKind" | "executionState", value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(name, value); else next.delete(name);
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">非同期実行履歴</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          実行状態を用途横断で確認します。操作証跡の確認は監査ログを利用してください。
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          {kintoneAppId ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-sm">
              <span>対象アプリ：アプリID {kintoneAppId}</span>
              <Button asChild variant="outline" size="sm"><Link to={`/console/app-bindings/${encodeURIComponent(kintoneAppId)}`}>アプリ設定へ戻る</Link></Button>
            </div>
          ) : null}
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="font-medium">用途</span>
              <select className="w-full rounded-md border border-input bg-background px-3 py-2" value={kind ?? ""} onChange={(event) => setFilter("executionKind", event.target.value)}>
                <option value="">すべて</option>
                {KINDS.map((value) => <option key={value} value={value}>{asynchronousExecutionKindLabel(value)}</option>)}
              </select>
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium">状態</span>
              <select className="w-full rounded-md border border-input bg-background px-3 py-2" value={state ?? ""} onChange={(event) => setFilter("executionState", event.target.value)}>
                <option value="">すべて</option>
                {STATES.map((value) => <option key={value} value={value}>{stateLabel(value)}</option>)}
              </select>
            </label>
          </div>

          {executions.error ? <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{executions.error}</div> : null}
          {executions.isLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />読み込み中</div>
          ) : executions.items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">該当する実行履歴はありません。</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead><tr className="border-b text-left text-muted-foreground"><th className="px-3 py-2">用途</th><th className="px-3 py-2">状態</th><th className="px-3 py-2">転送確定</th><th className="px-3 py-2">理由</th><th className="px-3 py-2">開始要求</th><th className="px-3 py-2">完了</th><th className="px-3 py-2" /></tr></thead>
                <tbody>{executions.items.map((execution) => (
                  <tr key={execution.executionId} className="border-b last:border-b-0">
                    <td className="px-3 py-3">{asynchronousExecutionKindLabel(execution.executionKind)}</td>
                    <td className="px-3 py-3"><AsynchronousExecutionStatePill state={execution.executionState} startedAt={execution.startedAt} /></td>
                    <td className="px-3 py-3">{attachmentTransferProgressText(execution.attachmentTransferProgress)}</td>
                    <td className="px-3 py-3">{asynchronousExecutionReasonLabel(execution.reasonCode)}</td>
                    <td className="px-3 py-3">{formatDateTime(execution.createdAt)}</td>
                    <td className="px-3 py-3">{formatDateTime(execution.finishedAt)}</td>
                    <td className="px-3 py-3 text-right"><Button asChild variant="outline" size="sm"><Link to={`/console/asynchronous-executions/${execution.executionId}${kintoneAppId ? `?kintoneAppId=${encodeURIComponent(kintoneAppId)}` : ""}`}>詳細</Link></Button></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" disabled={!executions.hasPrev} onClick={executions.goPrev}>前へ</Button>
            <Button type="button" variant="outline" size="sm" disabled={!executions.hasNext} onClick={executions.goNext}>次へ</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function asKind(value: string | null): AsynchronousExecutionKind | undefined {
  return KINDS.includes(value as AsynchronousExecutionKind) ? value as AsynchronousExecutionKind : undefined;
}
function asState(value: string | null): AsynchronousExecutionState | undefined {
  return STATES.includes(value as AsynchronousExecutionState) ? value as AsynchronousExecutionState : undefined;
}
function stateLabel(state: AsynchronousExecutionState) {
  return ({ queued: "待機中", running: "実行中", blocked: "要対応", cancel_requested: "取消処理中", completed: "完了", failed: "失敗", canceled: "取消済み" } as const)[state];
}
